import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import AuthModal from "./AuthModal";
import PlanModal from "./PlanModal";
import UsageModal from "./UsageModal";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * App-level modals (login / plans / usage).
 * Always mounted under AuthProvider (see index.js), never inside editor panes
 * that use overflow:hidden — overlays are portaled to document.body.
 */
export default function GlobalModals() {
  const {
    planModalOpen,
    closePlanModal,
    usageModalOpen,
    closeUsageModal,
    loginModalOpen,
    billingNotice,
    clearBillingNotice,
  } = useAuth();
  const { texts } = useLanguage();
  const t = texts.billing || {};

  const anyOpen = Boolean(planModalOpen || usageModalOpen || loginModalOpen);

  useEffect(() => {
    if (!billingNotice) return undefined;
    const timer = setTimeout(() => clearBillingNotice(), 6000);
    return () => clearTimeout(timer);
  }, [billingNotice, clearBillingNotice]);

  // Prevent background scroll while a modal is open
  useEffect(() => {
    if (!anyOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [anyOpen]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <AuthModal />
      <PlanModal open={planModalOpen} onClose={closePlanModal} />
      <UsageModal open={usageModalOpen} onClose={closeUsageModal} />
      {billingNotice ? (
        <div
          className={`billing-toast billing-toast--${billingNotice}`}
          role="status"
          onClick={clearBillingNotice}
        >
          {billingNotice === "success"
            ? t.success || "お支払いが完了しました。プランを更新しています…"
            : t.canceled || "お支払いをキャンセルしました。"}
        </div>
      ) : null}
      <style>{`
        .billing-toast {
          position: fixed;
          left: 50%;
          bottom: 1.25rem;
          transform: translateX(-50%);
          z-index: 12000;
          max-width: min(480px, calc(100vw - 2rem));
          padding: 0.85rem 1.1rem;
          border-radius: 10px;
          box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
          font-size: 0.92rem;
          font-weight: 600;
          cursor: pointer;
          text-align: center;
        }
        .billing-toast--success {
          background: #ecfdf5;
          color: #047857;
          border: 1px solid #6ee7b7;
        }
        .billing-toast--cancel {
          background: #fff7ed;
          color: #c2410c;
          border: 1px solid #fdba74;
        }
      `}</style>
    </>,
    document.body
  );
}
